import {Component, OnDestroy, OnInit} from '@angular/core';
import {ProjectService} from '@catcore/services';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {ProjectEvidFunction, ProjectEvidVid} from '@catmodels/project-evid-functions/types';
import {SelectItem} from 'primeng/primeng';
import {Subscription} from 'rxjs/Subscription';
import {isNullOrUndefined} from 'util';
import {EntityForm} from "@catuimodels/shared/entity-form";
import {FormBuilder} from "@angular/forms";
import {EntityTypes} from "@catmodels/linked-entities/types";
import {ProjectEvidFunctionService} from "@catmodels/project-evid-functions/services/project-evid-function/project-evid-function.service";
import {extractDataFromModel, sortByLabelFunc} from "@catcore/utils";
import {GenericLinkedResource} from "@catcore/types/base";
import {ModifyParameterComponent} from "@catmodels/parameters/components/modify-parameter/modify-parameter.component";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {ModifyEvidVidComponent} from "@catmodels/project-evid-functions/components/modify-evid-vid/modify-evid-vid.component";

@Component({
  templateUrl: './project-evid-functions-landing.component.html',
  styleUrls: ['./project-evid-functions-landing.component.scss'],
})
export class ProjectEvidFunctionsLandingComponent extends EntityForm implements OnInit, OnDestroy {

  LinkedEntityTypes = EntityTypes;

  evidVids: Array<any> = [];

  linkedPorts: Array<{ href: string }> = [];
  linkedUnits: Array<{ href: string }> = [];
  linkedServices: Array<{ href: string }> = [];
  linkedVids: Array<{ href: string }> = [];
  linkedGuiZones: Array<{ href: string }> = [];
  evidServices: SelectItem[] = [];

  modalResourceSubscription: Subscription;

  fileList: any[] = [];

  private projectSubscription: Subscription;
  private servicesSubscription: Subscription;
  private fileListSubscription: Subscription;
  private currentResourceService;

  constructor(
    formBuilder: FormBuilder,
    private genericService: GenericResourceService,
    private projectService: ProjectService,
    private evidFunctionService: ProjectEvidFunctionService,
    private modalService: ModalService
  ) {
    super(formBuilder);
    if (this.form && this.formData._links.service) {
      this.form.patchValue({service: this.formData._links.service.href});
    }
  }

  ngOnInit() {
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.servicesSubscription = this.projectService
            .getAllServices(project, 'EVID')
            .subscribe((services) => {
              this.updateEvidServiceSelection(services);
            });

          this.fileListSubscription = this.projectService.getUploadFiles(project)
            .subscribe((files) => {
              extractDataFromModel(files, 'item', '_embedded').forEach((file) => {
                this.fileList.push({label: file.filename, value: file._links.self.href});
              });
              this.fileList.push({label: 'empty', value: null});
            });
        } else {
          this.fileList = [];
        }
      });
  }

  ngOnDestroy() {
    if (!isNullOrUndefined(this.servicesSubscription)) {
      this.servicesSubscription.unsubscribe();
    }
    if (!isNullOrUndefined(this.projectSubscription)) {
      this.projectSubscription.unsubscribe();
    }
    if (!isNullOrUndefined(this.fileListSubscription)) {
      this.fileListSubscription.unsubscribe();
    }
  }

  protected updateEvidVids() {
    this.evidVids = [];
    if (this.formData && this.formData._links.hasOwnProperty('evid_vids')) {
      this.formData._links.evid_vids.forEach((evidVid) => {
        this.evidVids.push({
          navigate: (router) => this.openModifyEvidVidDialog(evidVid),
          ...evidVid
        });
      });
      this.evidVids.sort(sortByLabelFunc);
    }
  }

  protected updateEvidServiceSelection(evidServices) {
    // build selection items from drop down
    const items: SelectItem[] = [];
    items.push({
      label: 'Auto-resolved by CAT',
      value: null,
    });
    evidServices.forEach((service) => {
      items.push({
        label: service.label,
        value: service._links.self.href,
      });
    });
    this.evidServices = items;
  }

  /**
   * Callback whenever the evid function resource is received from backend.
   * @param {ProjectEvidFunction} evidFunction
   */
  protected onEntityReceived(evidFunction: ProjectEvidFunction) {
    this.currentResourceService = this.genericService.findServiceForResource(this.resourceUri);
    if (this.form && evidFunction._links.service) {
      this.form.patchValue({service: evidFunction._links.service.href});
    }
    this.updateLinkedEntities(evidFunction);
  }

  protected refreshResource() {
    this.currentResourceService.refresh();
  }

  private updateLinkedEntities(evidFunction) {
    this.refreshLinkedPorts(evidFunction);
    this.refreshLinkedUnits(evidFunction);
    this.refreshLinkedServices(evidFunction);
    this.refreshLinkedVids(evidFunction);
    this.refreshLinkedGuiZones(evidFunction);
    this.updateEvidVids();
  }

  private refreshLinkedPorts(evidFunction: ProjectEvidFunction) {
    const extractedInfo = this.evidFunctionService.extractLinkedPortsFor(evidFunction);
    this.linkedPorts = extractedInfo;
  }

  private refreshLinkedUnits(evidFunction: ProjectEvidFunction) {
    const extractedInfo = this.evidFunctionService.extractLinkedUnitsFor(evidFunction);
    this.linkedUnits = extractedInfo;
  }

  private refreshLinkedServices(evidFunction: ProjectEvidFunction) {
    const extractedInfo = this.evidFunctionService.extractLinkedServicesFor(evidFunction);
    this.linkedServices = extractedInfo;
  }

  private refreshLinkedVids(evidFunction: ProjectEvidFunction) {
    const extractedInfo = this.evidFunctionService.extractLinkedVidsFor(evidFunction);
    this.linkedVids = extractedInfo;
  }

  private refreshLinkedGuiZones(evidFunction: ProjectEvidFunction) {
    const extractedInfo = this.evidFunctionService.extractLinkedGuiZonesFor(evidFunction);
    this.linkedGuiZones = extractedInfo;
  }

  /**
   *
   * @param {GenericLinkedResource} resource
   */
  private openModifyEvidVidDialog(resource: GenericLinkedResource) {
    this.openDialog(resource, (res) => this.resourceEvidVidModalFactory(res));
  }

  /**
   *
   * @returns {ModalRef}
   * @param evidVid
   */
  private resourceEvidVidModalFactory(evidVid) {
    const compRef = this.modalService.open(ModifyEvidVidComponent);
    compRef.componentInstance.evidVid = evidVid;
    compRef.componentInstance.afterUpdate.subscribe((evidVid) => this.onEvidVidUpdate(evidVid));
    return compRef;
  }

  /**
   *
   * @param {GenericLinkedResource} resource
   * @param componentFactory
   */
  protected openDialog(resource: GenericLinkedResource, componentFactory) {
    let compRef;
    const onFullfill = () => {
      resourceHandler.refresh();
      this.modalResourceSubscription.unsubscribe();
    };

    const onError = () => {
      this.modalResourceSubscription.unsubscribe();
    };

    const resourceHandler = this
      .genericService
      .findServiceForResource(resource.href);

    this.modalResourceSubscription = resourceHandler
      .loadAndGet()
      .subscribe((item) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(item)) {
          compRef = null;
        } else {
          compRef = componentFactory(item);
          compRef.result.then(onFullfill, onError);
        }
      });
  }

  private onEvidVidUpdate(evidVid: ProjectEvidVid) {
    this.evidVids
      .filter((vid) => vid.href === evidVid._links.self.href)
      .forEach((vid) => {
        vid.label = evidVid.label;
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/project-evid-functions/components/project-evid-functions-landing/project-evid-functions-landing.component.ts